import { useLanguage } from '@/contexts/LanguageContext';
import { usePosts } from '@/contexts/PostsContext';
import { useAdmin } from '@/contexts/AdminContext';
import { Link } from 'react-router-dom';

const CATEGORY_COLORS: Record<string, string> = {
  'Global Affairs': 'bg-teal-50 text-teal-600',
  'International Order': 'bg-stone-100 text-stone-600',
  'Rights and Freedoms': 'bg-rose-50 text-rose-600',
  'Politics': 'bg-amber-50 text-amber-600',
  'Justice and Freedom': 'bg-neutral-100 text-neutral-600',
  'Law and Society': 'bg-stone-100 text-stone-600',
};

const LatestIssues = () => {
  const { t, lang } = useLanguage();
  const { posts } = usePosts();
  const { isAdmin } = useAdmin();
  const latest = [...posts]
    .filter((p) => p.isPublic || isAdmin)
    .filter((p) => p.language === 'both' || p.language === lang)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 4);

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-2">
              {t('Recent', '최신')}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800">
              {t('Latest Posts', '최근 게시물')}
            </h2>
          </div>
        </div>

        <div className="space-y-0 divide-y divide-stone-50">
          {latest.map((post, i) => (
            <Link
              key={post.id}
              to={`/issues/${post.slug}`}
              className="group flex flex-col sm:flex-row gap-4 py-6 hover:bg-stone-50/50 rounded-xl px-3 -mx-3 transition-colors cursor-pointer"
            >
              <div className="text-3xl font-bold text-stone-100 w-10 shrink-0 mt-1 hidden sm:block">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="w-full sm:w-24 h-16 sm:h-16 shrink-0 overflow-hidden rounded-lg bg-stone-100">
                <img
                  src={post.coverImage}
                  alt={post.title[lang]}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[post.category] || 'bg-stone-100 text-stone-600'}`}>
                    {post.category}
                  </span>
                  <span className="text-xs text-stone-400">{post.date}</span>
                </div>
                <h3 className="text-sm font-bold text-stone-800 line-clamp-1 group-hover:text-stone-600 transition-colors">
                  {post.title[lang]}
                </h3>
                <p className="text-xs text-stone-500 line-clamp-1 mt-1">{post.summary[lang]}</p>
              </div>
              <div className="flex items-center gap-1 text-stone-400 shrink-0 self-center">
                <span className="text-xs">{post.readTime} min</span>
                <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LatestIssues;